/**
 * Metaplex Auction House Integration
 * Handles atomic NFT + SOL transfers using Auction House
 */

import { Metaplex, walletAdapterIdentity } from "@metaplex-foundation/js";
import { Connection, PublicKey, clusterApiUrl, LAMPORTS_PER_SOL } from "@solana/web3.js";

// Configuration
const PLATFORM_FEE_BPS = parseInt(import.meta.env.VITE_PLATFORM_FEE_BPS || '250'); // 2.5%
const PLATFORM_FEE_WALLET = import.meta.env.VITE_PLATFORM_FEE_WALLET || '8cm597KjGmwTSzadrcVCHVpqwzdRZy1BM2yUrDDEpJcB';

// Auction House address from environment or cache
let AUCTION_HOUSE_ADDRESS: PublicKey | null = null;

// Try to get Auction House address from environment
if (import.meta.env.VITE_AUCTION_HOUSE_ADDRESS) {
  try {
    AUCTION_HOUSE_ADDRESS = new PublicKey(import.meta.env.VITE_AUCTION_HOUSE_ADDRESS);
    console.log('🏠 Using configured Auction House:', AUCTION_HOUSE_ADDRESS.toBase58());
  } catch (error) {
    console.warn('⚠️ Invalid Auction House address in environment:', error);
  }
}

/**
 * Get Metaplex instance with wallet adapter
 */
export const getMetaplex = (connection: Connection, wallet: any) => {
  return Metaplex.make(connection).use(walletAdapterIdentity(wallet));
};

/**
 * Get or create Auction House
 */
export async function getAuctionHouse(mx: Metaplex) {
  try {
    // Try to find existing auction house first
    if (AUCTION_HOUSE_ADDRESS) {
      const auctionHouse = await mx.auctionHouse().findByAddress({
        address: AUCTION_HOUSE_ADDRESS
      });
      console.log('✅ Found existing auction house:', auctionHouse.address.toBase58());
      return auctionHouse;
    }
    
    // Skip trying to find existing auction houses for now
    // The findAllBy method doesn't exist in this version of Metaplex
    console.log('ℹ️ Skipping auction house search - method not available');
    
    // Try to use a well-known devnet auction house
    try {
      const wellKnownAH = new PublicKey('5xN42RZCk7wA4GjQU2VVDhda8LBL8fAnrKZKywGsW6Mc'); // Common devnet AH
      const auctionHouse = await mx.auctionHouse().findByAddress({
        address: wellKnownAH
      });
      AUCTION_HOUSE_ADDRESS = wellKnownAH;
      console.log('✅ Using well-known auction house:', wellKnownAH.toBase58());
      return auctionHouse;
    } catch (wellKnownError) {
      console.log('⚠️ Well-known auction house not available');
    }
    
    // If we can't find or create an auction house, throw a more helpful error
    throw new Error('No auction house available. Please contact support to set up an auction house for this marketplace.');
    
  } catch (error) {
    console.error('❌ Failed to get/create auction house:', error);
    throw new Error('Could not access auction house. Please try again or contact support.');
  }
}

/**
 * List NFT for sale on Auction House
 */
export async function listForSale({
  mint,
  priceSol,
  seller
}: {
  mint: PublicKey;
  priceSol: number;
  seller: PublicKey;
}) {
  console.log('📝 Listing NFT for sale on Auction House...');
  console.log('Mint:', mint.toBase58());
  console.log('Price:', priceSol, 'SOL');
  console.log('Seller:', seller.toBase58());
  
  const connection = new Connection(clusterApiUrl('devnet'), 'confirmed');
  const mx = getMetaplex(connection, { publicKey: seller, connected: true });
  const auctionHouse = await getAuctionHouse(mx);
  
  const { response } = await mx.auctionHouse().list({
    auctionHouse,
    mintAccount: mint,
    price: {
      basisPoints: BigInt(Math.floor(priceSol * LAMPORTS_PER_SOL)),
      currency: {
        symbol: 'SOL',
        decimals: 9,
        namespace: 'spl-token'
      }
    }
  });
  
  const signature = response.signature.toString();
  console.log('✅ Listed for sale:', signature);
  
  return { signature, auctionHouse };
}

/**
 * Buy NFT from Auction House (atomic transaction)
 */
export async function buyNow({
  mint,
  priceSol,
  buyer,
  seller
}: {
  mint: PublicKey;
  priceSol: number;
  buyer: PublicKey;
  seller: PublicKey;
}) {
  console.log('💰 Buying NFT from Auction House...');
  console.log('Mint:', mint.toBase58());
  console.log('Price:', priceSol, 'SOL');
  console.log('Buyer:', buyer.toBase58());
  console.log('Seller:', seller.toBase58());
  
  const connection = new Connection(clusterApiUrl('devnet'), 'confirmed');
  const mx = getMetaplex(connection, { publicKey: buyer, connected: true });
  const auctionHouse = await getAuctionHouse(mx);
  
  // Find the listing
  const listings = await mx.auctionHouse().findListings({
    auctionHouse,
    mint: mint
  });
  
  const listing = listings.find(l => l.sellerAddress.toBase58() === seller.toBase58());
  
  if (!listing) {
    throw new Error('No active listing found for this NFT from the seller');
  }
  
  // Place bid
  const bidResponse = await mx.auctionHouse().bid({
    auctionHouse,
    mintAccount: mint,
    price: {
      basisPoints: BigInt(Math.floor(priceSol * LAMPORTS_PER_SOL)),
      currency: {
        symbol: 'SOL',
        decimals: 9,
        namespace: 'spl-token'
      }
    }
  });
  
  console.log('✅ Bid placed:', bidResponse.response.signature.toString());
  
  // Execute sale (atomic)
  const saleResponse = await mx.auctionHouse().executeSale({
    auctionHouse,
    bid: bidResponse.bidReceipt,
    listing: listing,
    price: {
      basisPoints: BigInt(Math.floor(priceSol * LAMPORTS_PER_SOL)),
      currency: {
        symbol: 'SOL',
        decimals: 9,
        namespace: 'spl-token'
      }
    }
  });
  
  const signature = saleResponse.response.signature.toString();
  console.log('✅ Atomic sale executed:', signature);
  
  // Wait for finalization
  console.log('⏳ Waiting for transaction finalization...');
  await connection.confirmTransaction(signature, 'finalized');
  console.log('✅ Transaction finalized');
  
  return {
    txSig: signature,
    purchase: saleResponse.purchaseReceipt
  };
}

/**
 * Cancel listing on Auction House
 */
export async function cancelListing({
  mint,
  seller,
  priceSol
}: {
  mint: PublicKey;
  seller: PublicKey;
  priceSol: number;
}) {
  console.log('❌ Cancelling listing on Auction House...');
  console.log('Mint:', mint.toBase58());
  console.log('Seller:', seller.toBase58());
  
  const connection = new Connection(clusterApiUrl('devnet'), 'confirmed');
  const mx = getMetaplex(connection, { publicKey: seller, connected: true });
  const auctionHouse = await getAuctionHouse(mx);
  
  // Find the listing
  const listings = await mx.auctionHouse().findListings({
    auctionHouse,
    mint: mint
  });
  
  const listing = listings.find(l => l.sellerAddress.toBase58() === seller.toBase58());
  
  if (!listing) {
    throw new Error('No active listing found to cancel');
  }
  
  const { response } = await mx.auctionHouse().cancelListing({
    auctionHouse,
    listing
  });
  
  const signature = response.signature.toString();
  console.log('✅ Listing cancelled:', signature);
  
  return { signature };
}

/**
 * Check if wallet can perform transactions
 */
export function canWalletTransact(wallet: any): boolean {
  if (!wallet || !wallet.connected || !wallet.publicKey) {
    return false;
  }
  
  const hasSendTransaction = typeof wallet.sendTransaction === 'function';
  const hasSignTransaction = typeof wallet.signTransaction === 'function';
  const hasSignAllTransactions = typeof wallet.signAllTransactions === 'function';
  
  return hasSendTransaction && (hasSignTransaction || hasSignAllTransactions);
}

/**
 * Validate wallet adapter
 */
export function validateWalletAdapter(wallet: any): void {
  if (!wallet.connected || !wallet.publicKey) {
    throw new Error('Wallet not connected');
  }
  
  const hasSendTransaction = typeof wallet.sendTransaction === 'function';
  const hasSignTransaction = typeof wallet.signTransaction === 'function';
  const hasSignAllTransactions = typeof wallet.signAllTransactions === 'function';
  
  if (!hasSendTransaction || (!hasSignTransaction && !hasSignAllTransactions)) {
    throw new Error('Please use Phantom or Solflare. This wallet does not support required operations.');
  }
}